import { z } from "zod"

export const AnomalyTypeEnum = {
  Spike: "Spike",
  Drop: "Drop",
} as const

export const AnomalySeverityEnum = {
  Critical: "Critical",
  High: "High",
  Medium: "Medium",
  Low: "Low",
} as const

export const AnomalyImpactEnum = {
  Positive: "Positive",
  Negative: "Negative",
} as const

export const AnomalyTypeSchema = z.enum(AnomalyTypeEnum)
export const AnomalySeveritySchema = z.enum(AnomalySeverityEnum)
export const AnomalyImpactSchema = z.enum(AnomalyImpactEnum)

export type AnomalyType = z.infer<typeof AnomalyTypeSchema>
export type AnomalySeverity = z.infer<typeof AnomalySeveritySchema>
export type AnomalyImpact = z.infer<typeof AnomalyImpactSchema>

export const AnomalySchema = z.object({
  type: AnomalyTypeSchema,
  severity: AnomalySeveritySchema,
  impact: AnomalyImpactSchema.describe(
    "Whether the anomaly is positive (good for business) or negative (bad for business)",
  ),
  metricPath: z
    .string()
    .describe(
      "Dot-notation path to the metric (e.g., 'growth.conversations.created' or 'campaigns.active')",
    ),
  label: z
    .string()
    .describe(
      "Simple field label for the metric (e.g., 'Created conversations', 'Messages', 'Support tickets'). Do NOT include 'Spike' or 'Drop' - that information is already in the type field.",
    ),
  description: z
    .string()
    .describe(
      "Human-readable description of the anomaly and its business impact. DO NOT include day indices, dates, or time references - focus only on what happened and why it matters to the business.",
    ),
  affectedDays: z
    .array(z.string().regex(/^\d{4}-\d{2}-\d{2}$/))
    .describe("Array of dates (YYYY-MM-DD format) where the anomaly is most pronounced"),
  baselineValue: z
    .number()
    .describe("Typical or average value before the anomaly (the calculated mean μ)"),
  currentValue: z.number().describe("Current value during the anomaly (the anomalous value)"),
})

const AnomalyWithBreakdownKeySchema = AnomalySchema.extend({
  breakdownKey: z.string().nullable().describe("Breakdown key for segmented metrics"),
})

export const AnomaliesResponseSchema = z.object({
  anomalies: z.array(AnomalySchema),
  summary: z
    .string()
    .nullable()
    .describe("Brief summary of the most significant anomalies detected"),
})

export const AnomaliesWithBreakdownKeyResponseSchema = z.object({
  anomalies: z.array(AnomalyWithBreakdownKeySchema),
  summary: z
    .string()
    .nullable()
    .describe("Brief summary of the most significant anomalies detected"),
})

export type Anomaly = z.infer<typeof AnomalySchema>
export type AnomaliesResponse = z.infer<typeof AnomaliesResponseSchema>
